import { Injectable } from "@angular/core";
import { ArrayHelper, FileLogger, Tools } from "@comunicare/comunicare-helper";
import {
  ACTION_DRUGS_TYPE,
  ACTION_STATUS_ENTITY,
  Entity,
  EntityDrug,
  IAppError,
  ICycleSchema,
  IDrugAction,
  IDrugInfo,
  IDrugIntakeNotif,
  IDrugsHistoric,
  IDrugStock,
  IEntitylink,
  IExportDrugIntakePdfBody,
  INotification,
  IQuantities,
  IStepwise,
  ITiming,
  KeyValue,
  NOTIFICATION_STATUS,
  NOTIFICATION_SYSTEM_STATUS,
  NOTIFICATION_TYPE,
  Reference,
  SCHEDULE_PERIOD,
  TIMING_CODES,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import * as moment from "moment";
import { combineLatest, Observable, of } from "rxjs";
import { concatMap } from "rxjs/operators";
import { FormsData } from "../helpers/formsData";
import { TimingData } from "../models/timingData.model";
import { DrugsApiService } from "./api/drugs-api.service";
import { DrugSchemaService } from "./drugSchema.service";
import { SessionService } from "./session.service";
import { ToolsService } from "./tools.service";
import { UserService } from "./user.service";

@Injectable({
  providedIn: "root",
})
export class DrugsService {
  constructor(
    private drugsApiService: DrugsApiService,
    private translateService: TranslateService,
    private sessionsService: SessionService,
    private userService: UserService,
    private formsData: FormsData
  ) {}

  public static getCibledNotification(drugId: string, notifications: INotification[]): INotification[] {
    return notifications.filter((n) => n.appId === drugId && moment(n.time).isSameOrBefore(moment(), "day"));
  }

  public static drugTaken(
    entity: IEntitylink,
    historic: IDrugsHistoric[],
    intakeDrugs: INotification[]
  ): {
    unknow: number;
    take: number;
    untake: number;
  } {
    const asNecessary = entity?.entityData?.frequency?.asNecessary;
    const entityId = entity._id;
    const drugHistoric = historic.filter((v) => v.entityId === entityId).reverse();
    const cibledNotif = DrugsService.getCibledNotification(entityId, intakeDrugs).filter(ArrayHelper.onlyExtraUniqueNotifications);
    const taken = cibledNotif.filter(
      (n) => n.status === NOTIFICATION_STATUS.ACCEPTED && (asNecessary ? n?.unscheduledIntake === true : n?.unscheduledIntake !== true)
    ).length;
    const canceled = cibledNotif.filter((n) => n.status === NOTIFICATION_STATUS.CANCELED).length;
    const total = asNecessary ? taken : DrugsService.getSupposedIntakes(entity.entityData, drugHistoric).length - canceled;

    const notTaken = cibledNotif.filter((n) => n.status === NOTIFICATION_STATUS.REJECTED).length;
    const unknow = total - taken - notTaken;
    const percentGreen = total ? (taken / total) * 100 : 0;
    const percentOrange = total ? (unknow / total) * 100 : 100;
    return {
      unknow: percentOrange,
      take: percentGreen,
      untake: total ? (notTaken / total) * 100 : 0,
    };
  }

  public static combineSupposedAndRealIntakes(supposedIntakes: INotification[], receivedIntakes: INotification[]): INotification[] {
    const combined: INotification[] = [];
    let prevDate = null;
    let i = -1;
    let k = 0;
    for (const s of supposedIntakes) {
      ++i;
      const d = moment(s.time);
      const date = d.format("YYYY MM DD");

      if (prevDate && prevDate === date) {
        continue;
      }
      prevDate = date;
      const realsSameDay = [];
      const firstK = receivedIntakes.findIndex((r) => moment(r.time).format("YYYY MM DD") === date);
      if (firstK > -1) {
        // Check that we did not miss any "taken" or "not taken" information:
        // (it could happen if the users made weird timing changes in between validating intakes)
        while (firstK > k) {
          combined.push(receivedIntakes[k]);
          ++k;
        }
        for (const r of receivedIntakes.slice(firstK)) {
          if (moment(r.time).format("YYYY MM DD") === date) {
            realsSameDay.push(r);
            ++k;
          } else {
            break;
          }
        }
        ++k;
      }
      const supposedSameDay = [];
      for (const r of supposedIntakes.slice(i)) {
        if (moment(r.time).format("YYYY MM DD") === date) {
          supposedSameDay.push(r);
        } else {
          break;
        }
      }
      // if there no field "moment" in the real notifications, it means it's the
      // old system and we can't use the supposed intakes. We will just rely on the
      // 'unknown' notifications saved in db.
      const oldSystem: boolean = realsSameDay.length > 0 && !Tools.isDefined(realsSameDay[0].moment);
      if (realsSameDay.length >= supposedSameDay.length || oldSystem) {
        combined.push(...realsSameDay.filter((n) => n.status !== NOTIFICATION_STATUS.CANCELED));
      } else {
        // try to find which 'real' notif corresponds to which 'supposed' one
        const foundIds: string[] = [];
        for (const s2 of supposedSameDay) {
          const same = realsSameDay.find((r) => r.moment === s2.moment);
          // keep the real one if we have it
          if (same) {
            if (same.status !== NOTIFICATION_STATUS.CANCELED) {
              combined.push(same);
              foundIds.push(same._id);
            }
          }
          // else use the supposed one
          else {
            combined.push(s2);
          }
        }
        // Check that we did not miss any "taken" or "not taken" information:
        // (it could happen if the users made weird timing changes in between validating intakes)
        const missed = realsSameDay.filter(
          (r) => r.status !== NOTIFICATION_STATUS.NONE && r.status !== NOTIFICATION_STATUS.CANCELED && !foundIds.includes(r._id)
        );
        if (missed.length > 0) {
          combined.push(...missed);
        }
      }
    }
    // add not corresponding intakes (i.e unscheduled intakes)
    receivedIntakes.forEach((r) => {
      if (combined.findIndex((c) => c.time === r.time) === -1 && r.status !== NOTIFICATION_STATUS.CANCELED) {
        combined.push(r);
      }
    });
    return combined.sort((a, b) => moment(b.time).valueOf() - moment(a.time).valueOf());
  }

  public static getSupposedIntakes(drug: EntityDrug, historic: IDrugsHistoric[]): INotification[] {
    const intakes: INotification[] = [];
    let currentTiming = drug.frequency;
    let startDate = moment(drug.frequency.boundsPeriod?.start).startOf("day");
    let endDate = moment().endOf("day");
    if (currentTiming.boundsPeriod?.end) {
      const drugEnd = moment(currentTiming.boundsPeriod.end).endOf("day");
      if (endDate.isAfter(moment(drugEnd))) {
        endDate = drugEnd;
      }
    }
    const cycle = drug.cycle;
    const stepwises = drug.stepwiseSchema?.stepwises;
    // Makes sure the historic is sorted with newest first
    historic.sort((a: IDrugsHistoric, b: IDrugsHistoric) => {
      if (a.date === b.date) {
        return 0;
      }
      if (moment(a.date).isAfter(moment(b.date))) {
        return -1;
      } else {
        return 1;
      }
    });

    for (const h of historic) {
      const freqActions = h.actions.filter(
        (a) =>
          a.actionType === ACTION_DRUGS_TYPE.UPDATE_FREQUENCY ||
          a.actionType === ACTION_DRUGS_TYPE.UPDATE_MOMENT ||
          a.actionType === ACTION_DRUGS_TYPE.UPDATE_DATE
      );
      // if we have frequency changes, we need to split the intakes into different periods:
      if (freqActions.length > 0) {
        const changeDate = moment(h.date).startOf("day");
        // compute the previous period's intake
        intakes.push(...this.computeIntakes(changeDate, endDate, currentTiming, cycle, stepwises));
        // start setting up the new period:
        endDate = changeDate.subtract(1, "days");
        currentTiming = this.getOldTimingFromHistoric(currentTiming, freqActions);
        startDate = moment(currentTiming.boundsPeriod.start).startOf("day");
        if (endDate.isBefore(startDate) && !currentTiming.endless) {
          endDate = startDate;
        }
      }
    }
    intakes.push(...this.computeIntakes(startDate, endDate, currentTiming, cycle, stepwises));

    // remove duplicates: a same day can have several intakes (several moments or fixed hours),
    // so we must dedupe on the (time, moment) pair
    return intakes.filter((item, i, arr) => arr.findIndex((x) => x.time === item.time && x.moment === item.moment) === i);
  }

  private static computeIntakes(
    startDate: moment.Moment,
    endDate: moment.Moment,
    timing: ITiming,
    cycle?: ICycleSchema,
    stepwises?: IStepwise[]
  ): INotification[] {
    const intakesNotifcations: INotification[] = [];
    if (stepwises?.length > 0) {
      intakesNotifcations.push(...this.computeIntakesStepwiseDrug(stepwises, timing, startDate, endDate));
    } else {
      const intakesDates: string[] =
        cycle && cycle.cycle.length > 0
          ? DrugSchemaService.getCycleInstances(cycle, timing, startDate, endDate)
          : TimingData.getTimingInstances(timing, startDate, endDate);
      const intakesPerDay = TimingData.getIntakesPerDay(timing);
      for (const d of intakesDates.reverse()) {
        for (const t of intakesPerDay) {
          const notif: INotification = {
            account: "",
            systemId: 0,
            appId: "",
            active: true,
            ntype: NOTIFICATION_TYPE.DRUG,
            message: "",
            time: d,
            moment: t,
            trigger: "",
            at: "",
            start: "",
            end: "",
            systemStatus: NOTIFICATION_SYSTEM_STATUS.SCHEDULED,
            status: NOTIFICATION_STATUS.NONE,
            comment: "",
            quantity: timing.timeAndQuantitiesOfDay?.find((tq) => tq.time === t)?.quantity ?? this.getMomentsQuantity(t, timing.quantities),
            hasDisplay: false,
          };
          intakesNotifcations.push(notif);
        }
      }
    }
    return intakesNotifcations;
  }

  private static computeIntakesStepwiseDrug(
    stepwises: IStepwise[],
    timing,
    startDate: moment.Moment,
    endDate: moment.Moment
  ): INotification[] {
    const notif: INotification[] = [];
    stepwises.forEach((stepwise) => {
      const startDay = moment(timing.boundsPeriod.start).add(stepwise.startDay, "d");
      stepwise.days.forEach((d, i) => {
        const frequency: ITiming = {
          boundsPeriod: {
            start: moment(Tools.deepCopy(startDay)).add(i, "d").format(),
          },
          period: 1,
          periodUnits: "d",
        };
        frequency.boundsPeriod.end = frequency.boundsPeriod.start;
        d.moment.forEach((m) => {
          frequency.timingCode = TIMING_CODES.find((t) => t.display === m.timingCode).value;
          const timingInstances = TimingData.getTimingInstances(frequency, startDate, endDate);
          m.drugs.forEach(() => {
            const intakesPerDay = TimingData.getIntakesPerDay(frequency);
            timingInstances.forEach((instance) => {
              for (const t of intakesPerDay) {
                notif.push({
                  account: "",
                  systemId: 0,
                  appId: "",
                  active: true,
                  ntype: NOTIFICATION_TYPE.DRUG,
                  message: "",
                  time: instance,
                  moment: t,
                  trigger: "",
                  at: "",
                  start: "",
                  end: "",
                  systemStatus: NOTIFICATION_SYSTEM_STATUS.SCHEDULED,
                  status: NOTIFICATION_STATUS.NONE,
                  comment: "",
                  quantity: this.getMomentsQuantity(t, timing.quantities),
                  hasDisplay: false,
                });
              }
            });
          });
        });
      });
    });
    return notif;
  }

  private static getOldTimingFromHistoric(currentTiming: ITiming, actions: IDrugAction[]) {
    const oldTiming: ITiming = Tools.deepCopy(currentTiming);
    for (const a of actions) {
      if (a.actionType === ACTION_DRUGS_TYPE.UPDATE_FREQUENCY) {
        return JSON.parse(a.exValue) as ITiming;
      } else if (a.actionType === ACTION_DRUGS_TYPE.UPDATE_MOMENT) {
        if (a.exValue?.includes(":")) {
          const times = a.exValue.split(", ");
          oldTiming.timeOfDay = times;
        } else {
          oldTiming.timingCode = a.exValue;
        }
      } else if (a.actionType === ACTION_DRUGS_TYPE.UPDATE_DATE) {
        // We need to know whether it's the start or end date !
        const isStartDate = a.newValue === currentTiming.boundsPeriod.start;
        if (isStartDate) {
          oldTiming.boundsPeriod.start = a.exValue;
        } else {
          oldTiming.boundsPeriod.end = a.exValue;
        }
      }
    }
    return oldTiming;
  }

  public list(patientId: string): Observable<IEntitylink[]> {
    const routeName = this.drugsApiService.readRoutes[0];
    return this.userService.isAuthorized(routeName, "GET").pipe(
      concatMap((isAuth) => {
        if (!isAuth) {
          FileLogger.warn("DrugsService", "User does not have access to: GET " + routeName);
          return of([]) as Observable<IEntitylink[]>;
        }
        return this.drugsApiService.list(patientId);
      })
    );
  }

  public listIntake(patientId: string, itemId?: string): Observable<INotification[]> {
    const routeName = this.drugsApiService.readRoutes[1];
    return this.userService.isAuthorized(routeName, "GET").pipe(
      concatMap((isAuth) => {
        if (!isAuth) {
          FileLogger.warn("DrugsService", "User does not have access to: GET " + routeName);
          return of([]) as Observable<INotification[]>;
        }
        return this.drugsApiService.listIntake(patientId, itemId);
      })
    );
  }

  public getOne(id: string): Promise<IEntitylink> {
    return this.drugsApiService.getOne(id).toPromise();
  }

  public save(drug: IEntitylink): Observable<number> {
    return this.drugsApiService.save(drug);
  }

  public delete(drug: IEntitylink): Observable<number> {
    drug.actionStatus = ACTION_STATUS_ENTITY.DELETED;
    return this.drugsApiService.save(drug);
  }

  public getHistoric(patientId: string): Observable<IDrugsHistoric[]> {
    const routeName = this.drugsApiService.readRoutes[4];
    return this.userService.isAuthorized(routeName, "GET").pipe(
      concatMap((isAuth) => {
        if (!isAuth) {
          FileLogger.warn("DrugsService", "User does not have access to: GET " + routeName);
          return of([]) as Observable<IDrugsHistoric[]>;
        }
        return this.drugsApiService.getHistoric(patientId);
      })
    );
  }

  public listInfo(query: string): Observable<IDrugInfo[]> {
    const routeName = this.drugsApiService.readRoutes[2];
    return this.userService.isAuthorized(routeName, "GET").pipe(
      concatMap((isAuth) => {
        if (!isAuth) {
          FileLogger.warn("DrugsService", "User does not have access to: GET " + routeName);
          return of([]) as Observable<IDrugInfo[]>;
        }
        return this.drugsApiService.listInfo(query, this.sessionsService.userLang);
      })
    );
  }

  /**
   * return short/long description for an activity
   */
  public computeFrequency(timing: ITiming, withShortLabel = false): string {
    if (!timing) {
      return "";
    }
    const freqOption = TimingData.getFreqOption(timing);
    const timingOption = TimingData.getTimingOption(timing);
    const hasTiming = TimingData.hasTiming(timing, freqOption, timingOption);
    // fix when drug have no timingCode (from careplan for now)
    if (!hasTiming) {
      return "";
    }
    let description = "";
    const timesPerDay = TimingData.getIntakesPerDay(timing, timingOption).length;
    const timesPerDayStr = timesPerDay > 0 ? timesPerDay.toString() : "";

    if (freqOption === TimingData.asNecessaryOption) {
      return this.translateService.instant(TimingData.asNecessaryOptionDisplay);
    } else if (freqOption === TimingData.fixedDaysOption) {
      const nbDays = timing.event.length;
      description += timesPerDayStr + "x/ " + nbDays + " " + this.translateService.instant(TimingData.fixedDaysOptionDisplay);
      if (!withShortLabel) {
        const dates = timing.event.map((e) => moment(e).format("DD-MM-YYYY"));
        description += " (" + dates.join(", ") + ")";
      }
      return description;
    }
    const period = TimingData.getScheduledPeriod(timing);
    let timingPeriod = 0;
    if (timing.period > 1) {
      timingPeriod = timing.period;
    }
    switch (period) {
      // minute aren't supported yet
      case SCHEDULE_PERIOD.MINUTE:
        description += this.translateService.instant(withShortLabel ? "periodUnit.minute.short" : "periodUnit.minute.plural");
        break;
      // HOUR : every X hours (if x=1, don't show)
      case SCHEDULE_PERIOD.HOUR:
        description += this.translateService.instant("periodUnit.everyF") + " " + (timingPeriod > 1 ? timingPeriod + " " : "");
        description += this.translateService.instant(withShortLabel ? "periodUnit.hour.short" : "periodUnit.hour.plural");
        break;
      // DAY : X x/d,  every Y days (if Y=1, don't show)
      case SCHEDULE_PERIOD.DAY:
        description +=
          timesPerDayStr +
          "x/" +
          this.translateService.instant("periodUnit.day.short") +
          " " +
          this.translateService.instant("periodUnit.everyM") +
          " " +
          (timingPeriod > 1 ? timingPeriod + " " : "");
        description += this.translateService.instant(withShortLabel ? "periodUnit.day.short" : "periodUnit.day.plural");
        break;
      // WEEK : X x/d, Yd every Z weeks (if Z=1, don't show)
      case SCHEDULE_PERIOD.WEEK:
        // add number per day
        description += timesPerDayStr + "x/" + this.translateService.instant("periodUnit.day.short") + ", ";
        // add number of days
        description +=
          this.getNbrDays(timing.when) +
          this.translateService.instant("periodUnit.day.short") +
          " " +
          this.translateService.instant("periodUnit.everyF") +
          " ";
        // add number of weeks
        description +=
          (timingPeriod > 1 ? timingPeriod + " " : "") +
          this.translateService.instant(withShortLabel ? "periodUnit.week.short" : "periodUnit.week.plural");
        // day
        description += ` (${this.getDayNames(timing.when)})`;
        break;
      // Month : Xx/d, Yd every Z month (if Z=1, don't show)
      case SCHEDULE_PERIOD.MONTH:
        // add number per day
        description += timesPerDayStr + "x/" + this.translateService.instant("periodUnit.day.short") + ", ";
        // add number of days
        description +=
          this.getNbrDays(timing.when) +
          this.translateService.instant("periodUnit.day.short") +
          " " +
          this.translateService.instant("periodUnit.everyM") +
          " ";
        // add number of months
        description +=
          (timingPeriod > 1 ? timingPeriod + " " : "") +
          this.translateService.instant(withShortLabel ? "periodUnit.month.short" : "periodUnit.month.plural");
        // day of Month
        description += ` (${this.getDayOfMonth(timing.when)})`;
        break;
      // year aren't supported yet
      case SCHEDULE_PERIOD.YEAR:
        description += this.translateService.instant(withShortLabel ? "periodUnit.year.short" : "periodUnit.year.plural");
        break;
    }

    if (timing.count && !withShortLabel) {
      description += " (max " + timing.count + "x)";
    }

    if (timing.duration && timing.durationUnit) {
      const availableEveryType: KeyValue[] = this.formsData.EVERY_TYPES;
      availableEveryType.push(
        new KeyValue({
          key: this.translateService.instant("periodUnit.year.singPlural"),
          value: "y",
        })
      );
      const unit = availableEveryType.find((kv) => kv.value === timing.durationUnit);
      description += ` (${this.translateService.instant("table.for")}  ${timing.duration} ${unit})`;
    }

    return description;
  }

  public getDayOfMonth(when: string): string {
    if (!when || when === "null") {
      return "";
    }
    let str = "";
    const arrWhen = (JSON.parse(when) as unknown[]).map((w) => String(w)).filter((v) => Tools.isDefined(v));

    arrWhen.forEach((v, i) => {
      str += v + (i < arrWhen.length - 1 ? ", " : "");
    });

    return str;
  }

  public getNbrDays(when: string): number {
    if (when && when !== "null") {
      return (JSON.parse(when) as unknown[]).map((w) => String(w)).filter((v) => Tools.isDefined(v)).length;
    } else {
      return 0;
    }
  }

  public getDayNames(when: string): string {
    if (!when || when === "null") {
      return "";
    }
    let str = "";
    const dayNames = this.keyDayNames();
    const arrWhen = (JSON.parse(when) as unknown[]).map((w) => String(w)).filter((v) => Tools.isDefined(v));

    const filtered = dayNames.filter((kv) => arrWhen.indexOf(kv.key) >= 0);

    filtered.forEach((kv, i) => {
      str += kv.value + (i < filtered.length - 1 ? ", " : "");
    });

    return str;
  }

  /**
   * return week days long name (in locale) and day number (1=monday, 7=sunday)
   */
  public keyDayNames(): KeyValue[] {
    const userLang = this.sessionsService.userLang;
    const dayNames = [];
    for (let i = 1; i <= 7; i++) {
      dayNames.push({
        key: i.toString(),
        value: moment().locale(userLang).isoWeekday(i).format("dddd"),
      });
    }
    return dayNames;
  }

  public getPerformerName(reference: string): string {
    switch (reference) {
      case "H":
        return this.translateService.instant("drugagenda.hospital");
      case "D":
        return this.translateService.instant("drugagenda.familydoctor");
      case "P":
        return this.translateService.instant("drugagenda.pharmacist");
    }
  }

  /**
   * Generates a string representation of the drug's administration timing,
   * based on its frequency settings.
   *
   * If the drug uses a "fixed hours" timing configuration, the method returns
   * a comma-separated list of times (e.g., "08:00, 12:00, 18:00").
   *
   * Otherwise, it returns a localized list of moments (e.g., "Morning", "Evening"),
   * optionally followed by the corresponding quantity (e.g., "Morning: 1 inhalation"),
   * one per line (using HTML line breaks).
   *
   * Also leverages `getMomentsQuantity` to handle moment-to-quantity mapping,
   * including normalization of keys like "rising" → "rise" or "beding" → "bedtime".
   *
   * @param {EntityDrug} entityDrug - The drug entity containing frequency, timing, and quantity data.
   * @returns {string} A formatted HTML string describing the timing and quantities for the drug.
   */
  public getTiming(entityDrug: EntityDrug): string {
    let str = "";
    if (!TimingData.hasTiming(entityDrug.frequency)) {
      return str;
    }
    if (TimingData.getTimingOption(entityDrug.frequency) === TimingData.fixedHoursTimingOption) {
      str =
        entityDrug.frequency.timeAndQuantitiesOfDay
          ?.map((t) => t.time + ": " + EntityDrug.computeQuantityDisplay(t.quantity, ToolsService.SUFFIX_QUANTITY))
          ?.join("<br>") ?? entityDrug.frequency.timeOfDay?.join(", ");
    } else {
      let nbrEl = 0;
      Entity.toKeyValues(entityDrug.frequency.timingCode).forEach((kv) => {
        if (kv.value) {
          const momentQuantity = DrugsService.getMomentsQuantity(kv.key, entityDrug?.frequency?.quantities);

          str +=
            (+nbrEl !== 0 ? "<br />" : "") +
            this.translateService.instant("mydrugs." + kv.key) +
            (momentQuantity ? ": " + EntityDrug.computeQuantityDisplay(momentQuantity, ToolsService.SUFFIX_QUANTITY) : "");
          nbrEl++;
        }
      });
    }
    return str;
  }

  /**
   * Transform TimingCode into 3 string
   */
  public getTimingFromTimingCode(timingCode: string): string {
    if (timingCode.includes(":")) {
      return timingCode;
    }
    let str = "";
    let nbrEl = 0;
    Entity.toKeyValues(timingCode).forEach((kv) => {
      if (kv.value) {
        str += (+nbrEl !== 0 ? ", " : "") + this.translateService.instant("mydrugs." + kv.key);
        nbrEl++;
      }
    });
    return str;
  }

  public async exportDrugs(patientId: string, lang: string): Promise<void> {
    this.drugsApiService.exportDrugs(patientId, lang).subscribe(
      (blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url);
      },
      (error: IAppError) => {
        FileLogger.error("DrugsService", "exportDrugs", error);
        throw new Error(error.message);
      }
    );
  }

  public addUnscheduledDrugIntake(
    patientId: string,
    drug: IEntitylink,
    intakeValidation: boolean,
    quantityTaken?: string,
    comment?: string,
    time?: string,
    momentOfDay?: string,
    administrationTime?: string,
    quantity?: string,
    allNotifications?: INotification[],
    isEditing = false
  ): Observable<INotification | INotification[]> {
    const author: Reference = {
      reference: this.sessionsService.account.caremateIdentifier,
      display: this.sessionsService.account.displayName,
    };
    const intakes: IDrugIntakeNotif[] = [
      {
        drugId: drug._id,
        patientId,
        intakeValidation: intakeValidation ? NOTIFICATION_STATUS.ACCEPTED : NOTIFICATION_STATUS.REJECTED,
        author: author,
        drugStart: drug.entityData.frequency?.boundsPeriod?.start,
        drugEnd: drug.entityData.frequency?.boundsPeriod?.end,
        comment,
        time: isEditing ? time : this.ensureUniqueNotificationTime(this.extractDrugIntakesInfo(allNotifications), drug, time, author),
        administrationTime,
        moment: momentOfDay,
        quantity,
        quantityTaken,
        unscheduledIntake: true,
      },
    ];
    this.manageStockAfterIntake(patientId, drug, quantityTaken); // not used yet, what to do when editing an intake ? CMATE-7662
    if (isEditing) {
      // edit the intake
      const intakes$ = [this.drugsApiService.updateDrugIntake(intakes[0])];
      intakes.splice(0, 1);
      if (intakes?.length) {
        // surplus : create new intakes
        intakes$.push(this.drugsApiService.createDrugIntakes(intakes));
      }
      return combineLatest(intakes$);
    }
    return this.drugsApiService.createDrugIntakes(intakes);
  }

  public addScheduledDrugIntake(
    patientId: string,
    drug: IEntitylink,
    intakeValidation: boolean,
    quantityTaken?: string,
    comment?: string,
    time?: string,
    momentOfDay?: string,
    administrationTime?: string,
    quantity?: string,
    allNotifications?: INotification[],
    isEditing = false
  ): Observable<INotification | INotification[]> {
    const author: Reference = {
      reference: this.sessionsService.account.caremateIdentifier,
      display: this.sessionsService.account.displayName,
    };
    const intakes: IDrugIntakeNotif[] = [
      {
        drugId: drug._id,
        patientId,
        intakeValidation: intakeValidation ? NOTIFICATION_STATUS.ACCEPTED : NOTIFICATION_STATUS.REJECTED,
        author: author,
        drugStart: drug.entityData.frequency?.boundsPeriod?.start,
        drugEnd: drug.entityData.frequency?.boundsPeriod?.end,
        comment,
        time: isEditing ? time : this.ensureUniqueNotificationTime(this.extractDrugIntakesInfo(allNotifications), drug, time, author),
        administrationTime,
        moment: momentOfDay,
        quantity,
        quantityTaken,
        unscheduledIntake: false,
      },
    ];

    // If the quantity taken is greater than the quantity scheduled:
    if (quantityTaken && quantity && Number.isFinite(Number(quantity)) && Number(quantityTaken) > Number(quantity)) {
      const surplus = Number(quantityTaken) - Number(quantity);
      if (surplus > 0) {
        intakes[0].quantityTaken = Tools.twoDigitNumber(+quantity);
        intakes.push({
          drugId: drug._id,
          patientId,
          intakeValidation: intakeValidation ? NOTIFICATION_STATUS.ACCEPTED : NOTIFICATION_STATUS.REJECTED,
          author: author,
          drugStart: drug.entityData.frequency?.boundsPeriod?.start,
          drugEnd: drug.entityData.frequency?.boundsPeriod?.end,
          comment,
          time: this.ensureUniqueNotificationTime(
            [...this.extractDrugIntakesInfo(allNotifications), ...this.extractDrugIntakesInfo([intakes[0]])],
            drug,
            time,
            author
          ),
          administrationTime,
          moment: momentOfDay,
          //we don't pass the quantity for unscheduledIntake
          quantityTaken: Tools.twoDigitNumber(surplus),
          unscheduledIntake: true,
        });
      }
    }
    this.manageStockAfterIntake(patientId, drug, quantityTaken); // not used yet, what to do when editing an intake ? CMATE-7662
    if (isEditing) {
      // edit the intake
      const intakes$ = [this.drugsApiService.updateDrugIntake(intakes[0])];
      intakes.splice(0, 1);
      if (intakes?.length) {
        // surplus : create new intakes
        intakes$.push(this.drugsApiService.createDrugIntakes(intakes));
      }
      return combineLatest(intakes$);
    }
    return this.drugsApiService.createDrugIntakes(intakes);
  }

  private manageStockAfterIntake(patientId: string, drug: IEntitylink, quantityTaken?: string): void {
    if (drug.entityData.managedStock && this.userService.isAuthorizedSync(null, this.drugsApiService.updateRoutes[0], "PUT")) {
      let totalRemainingUsagesInc = -Number(quantityTaken);
      let totalUsagesInc = 0;
      if (drug.entityData.stock) {
        totalRemainingUsagesInc = drug.entityData.stock.totalRemainingUsages - Number(quantityTaken);
        totalUsagesInc = drug.entityData.stock.totalUsages;
        drug.entityData.stock = null;
        this.save(drug).subscribe({
          error: (err) => {
            console.error("Error while removing old stock: ", err);
          },
        });
      }
      this.drugsApiService
        .updateDrugStock({
          patientId,
          drugId: drug._id,
          totalRemainingUsagesInc,
          totalUsagesInc,
        })
        .subscribe({
          error: (err) => {
            console.error("Error while updating stock: ", err);
          },
        });
    }
  }

  public getDrugInfo(drug: EntityDrug): Observable<IDrugInfo> {
    return this.drugsApiService.getDrugInfo(drug.reference, drug.source);
  }

  /**
   * Retrieves the dosage quantity for a specific moment of the day from a IQuantities object.
   *
   * This method also handles common mismatches between expected keys
   * and actual values by mapping:
   * - "rising" → "rise"
   * - "beding" → "bedtime"
   *
   * @param {string} moment - The moment of the day (e.g., "rising", "beding", "noon", etc.).
   * @param {IQuantities} quantities - the timing quantities
   * @returns {string} The quantity to take at the specified moment
   */
  public static getMomentsQuantity(moment: string, quantities: IQuantities): string {
    let quantity: string;
    switch (moment) {
      case "rising":
        quantity = quantities?.rise;
        break;
      case "beding":
        quantity = quantities?.bedtime;
        break;
      default:
        quantity = quantities?.[moment];
        break;
    }
    return quantity ?? "";
  }

  public async getDrugStock(patientId: string, drugId: string): Promise<IDrugStock> {
    return this.drugsApiService.getDrugStock(patientId, drugId).toPromise();
  }

  public async createDrugIntakesPDF(drugIntakePdfBody: IExportDrugIntakePdfBody): Promise<void> {
    return new Promise((resolve) => {
      this.drugsApiService.createDrugIntakesPDF(drugIntakePdfBody).subscribe(
        (blob) => {
          const url = URL.createObjectURL(blob);
          window.open(url);
          resolve();
        },
        (error: IAppError) => {
          FileLogger.error("DrugsService", "createDrugIntakesPDF", error);
          resolve();
        }
      );
    });
  }

  public isInfinite(date: string): boolean {
    if (date && date.length && date[0] === "9") {
      return true;
    }
  }

  public getFormatDate(d: Date): string {
    const date = new Date(d);
    return date.getDate() + "-" + (date.getMonth() + 1) + "-" + date.getFullYear();
  }

  /**
   * Checks if the notification time is unique by comparing existing notifications.
   * If a notification with the same (drugId, time, author) already exists,
   * it increments the time by one second until a unique time is found.
   *
   * @param allNotifications - An array of notifications to check against.
   * @param drug - The `drug` object containing the medication ID (`_id`) used for indexing.
   * @param time - The initially proposed time for the notification.
   * @param author - The author of the notification, used for comparison.
   * @returns A unique time after verification.
   */
  private ensureUniqueNotificationTime(
    allNotifications: { drugId: string; time: string; author: Reference }[],
    drug: IEntitylink,
    time: string,
    author: Reference
  ): string {
    while (allNotifications.find((n) => n.drugId === drug._id && n.time === time && n.author?.reference === author?.reference)) {
      time = moment(time).add(1, "second").format();
    }
    return time;
  }

  /**
   * Extracts the essential information from each notification or drug intake.
   * This method returns an array of objects containing the drug ID, time, and author.
   *
   * @param notifs - An array of notifications or drug intake notifications to transform.
   * @returns An array of objects containing `drugId`, `time`, and `author` properties for each notification.
   */
  private extractDrugIntakesInfo(notifs: (INotification | IDrugIntakeNotif)[]): { drugId: string; time: string; author: Reference }[] {
    return notifs.map((notif) => ({
      drugId: "appId" in notif ? notif.appId : "drugId" in notif ? notif.drugId : "",
      time: notif.time,
      author: notif.author,
    }));
  }

  public cancelScheduledDrugIntake(patientId: string, drug: IEntitylink, time: string, momentOfDay: string): Observable<INotification> {
    const author: Reference = {
      reference: this.sessionsService.account.caremateIdentifier,
      display: this.sessionsService.account.displayName,
    };
    const intakes: IDrugIntakeNotif[] = [
      {
        drugId: drug._id,
        patientId,
        intakeValidation: NOTIFICATION_STATUS.CANCELED,
        author: author,
        drugStart: drug.entityData.frequency?.boundsPeriod?.start,
        drugEnd: drug.entityData.frequency?.boundsPeriod?.end,
        time: time,
        moment: momentOfDay,
      },
    ];
    return this.drugsApiService.createDrugIntakes(intakes);
  }
}
